import React, { useEffect, useState } from 'react';
import { Upload, Modal, message, Row } from 'antd';
import { RcFile, UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import { CloudUploadOutlined } from '@ant-design/icons/lib';
import { Area } from 'react-easy-crop';
import { checkImage } from 'common/helpers/loader.helper';
import { EFileStatus, IImageModel } from 'common/components/Image/Image.models';
import { EErrorStatus } from 'common/models/requestModels';
import { getCroppedWebpFile, getImageDimensions, matchesTargetRatio } from 'common/helpers/image.processor';
import { ImageCropModal } from 'common/components/Image/ImageCropModal';
import { useAppSelector } from 'app/store/store.hooks';

interface IComponentProps {
  disabled?: boolean;
  onChange?: (imageId: string | null) => void;
  value?: string;
  targetWidth: number;
  targetHeight: number;
}

interface IPendingCrop {
  file: RcFile;
  imageSrc: string;
  resolve: (result: File | typeof Upload.LIST_IGNORE) => void;
}

export const ImageUpload: React.FC<IComponentProps> = ({ disabled, onChange, value, targetWidth, targetHeight }) => {
  const { authModel } = useAppSelector((state) => state.auth);
  const [isPreviewVisible, setIsPreviewVisible] = useState<boolean>(false);
  const [imageId, setImageId] = useState<string>('');
  const [fileList, setFileList] = useState<UploadFile<IImageModel>[]>([]);
  const [pendingCrop, setPendingCrop] = useState<IPendingCrop | null>(null);

  useEffect(() => {
    initValue(value);
  }, [value]);

  const handleCancel = () => setIsPreviewVisible(false);

  const handlePreview = (file: UploadFile<IImageModel>) => {
    const imageId = file.response?.id || value;

    if (imageId) {
      setImageId(imageId);
      setIsPreviewVisible(true);
    }
  };

  const initValue = (value?: string) => {
    const fileList: UploadFile<IImageModel>[] = value
      ? [
          {
            uid: '1',
            name: 'image',
            status: 'done',
            size: 1,
            type: '',
            url: `${window.location.origin}/api/images/${value}`,
          },
        ]
      : [];

    setFileList(fileList);
  };

  const handleChange = (info: UploadChangeParam<UploadFile<IImageModel>>) => {
    const { file } = info;
    const { status, response } = file;

    if (file.status === EFileStatus.Removed && onChange) {
      setFileList([]);
      onChange(null);

      return;
    }

    if (status === EFileStatus.Done) {
      const imageId = response?.id;

      setFileList([file]);

      if (onChange) {
        onChange(imageId || null);
      }

      return;
    }
    if (status === EFileStatus.Error) {
      if (file.error?.status === EErrorStatus.Unauthorized) {
        message.warning('Something went wrong, reload page and try again');
      } else {
        message.error('Image upload failed, please try again');
      }
    }

    setFileList([file]);
  };

  const beforeUpload = (file: RcFile): Promise<File | typeof Upload.LIST_IGNORE> => {
    if (!checkImage(file)) {
      return Promise.resolve(Upload.LIST_IGNORE);
    }

    return getImageDimensions(file).then(({ width, height }) => {
      const imageSrc = URL.createObjectURL(file);

      if (matchesTargetRatio(width, height, targetWidth, targetHeight)) {
        return getCroppedWebpFile(
          imageSrc,
          { x: 0, y: 0, width, height },
          targetWidth,
          targetHeight,
          file.name,
        ).finally(() => URL.revokeObjectURL(imageSrc));
      }

      return new Promise<File | typeof Upload.LIST_IGNORE>((resolve) => {
        setPendingCrop({ file, imageSrc, resolve });
      });
    });
  };

  const handleCropConfirm = async (cropPixels: Area) => {
    if (!pendingCrop) {
      return;
    }

    const { file, imageSrc, resolve } = pendingCrop;

    try {
      const webpFile = await getCroppedWebpFile(imageSrc, cropPixels, targetWidth, targetHeight, file.name);

      resolve(webpFile);
    } catch {
      message.error('Image processing failed, please try again');
      resolve(Upload.LIST_IGNORE);
    } finally {
      URL.revokeObjectURL(imageSrc);
      setPendingCrop(null);
    }
  };

  const handleCropCancel = () => {
    if (!pendingCrop) {
      return;
    }

    pendingCrop.resolve(Upload.LIST_IGNORE);
    URL.revokeObjectURL(pendingCrop.imageSrc);
    setPendingCrop(null);
  };

  const token = authModel?.access.token;

  const uploadButton = (
    <Row justify="center" align="middle">
      <CloudUploadOutlined />
      <div className="ant-upload-text width-full">Upload</div>
    </Row>
  );
  return (
    <>
      <Upload
        action="/api/images"
        headers={{ authorization: `Bearer ${token}` }}
        listType="picture-card"
        fileList={fileList}
        onPreview={handlePreview}
        onChange={handleChange}
        name="image"
        beforeUpload={beforeUpload}
        showUploadList={{
          showPreviewIcon: true,
          showDownloadIcon: false,
          showRemoveIcon: true,
        }}
        disabled={disabled}
      >
        {fileList.length >= 1 ? null : uploadButton}
      </Upload>
      <Modal width={640} open={isPreviewVisible} footer={null} onCancel={handleCancel}>
        <img alt="example" className="width-full p-6" src={`/api/images/${imageId}`} />
      </Modal>
      {pendingCrop && (
        <ImageCropModal
          open
          imageSrc={pendingCrop.imageSrc}
          aspectRatio={targetWidth / targetHeight}
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
        />
      )}
    </>
  );
};
